import { z } from 'zod';
import { ValueEntryNode } from './value-entry-node-schema';

/**
 * Lenient schema for deserializing ValueHistoryNode from storage
 */
const ValueHistoryNodeDeserializeSchema = z.object({
  bought: z.unknown().transform(val => val === undefined ? undefined : ValueEntryNode.deserialize(val)).optional(),
  history: z.array(z.unknown().transform(val => ValueEntryNode.deserialize(val))).catch([]),
  sold: z.unknown().transform(val => val === undefined ? undefined : ValueEntryNode.deserialize(val)).optional()
}).transform(val => ({
  bought: val.bought,
  history: val.history,
  sold: val.sold
}));

/**
 * Strict schema for creating new ValueHistoryNode
 */
const ValueHistoryNodeCreateSchema = z.object({
  bought: z.unknown().transform(val => val === undefined ? undefined : ValueEntryNode.create(val as Partial<ValueEntryNode>)).optional(),
  history: z.array(z.unknown().transform(val => ValueEntryNode.create(val as Partial<ValueEntryNode>))),
  sold: z.unknown().transform(val => val === undefined ? undefined : ValueEntryNode.create(val as Partial<ValueEntryNode>)).optional()
}).transform(val => ({
  bought: val.bought,
  history: val.history,
  sold: val.sold
}));

/**
 * ValueHistoryNode interface
 * Tracks purchase, historical values, and sale of an asset
 */
export interface ValueHistoryNode {
  bought?: ValueEntryNode;
  history: ValueEntryNode[];
  sold?: ValueEntryNode;
}

/**
 * ValueHistoryNode namespace with factory and deserialization methods
 */
export namespace ValueHistoryNode {
  export function deserialize(data: unknown): ValueHistoryNode {
    return ValueHistoryNodeDeserializeSchema.parse(data);
  }

  export function deserializeSafe(data: unknown) {
    const result = ValueHistoryNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  export function create(data: Partial<ValueHistoryNode>): ValueHistoryNode {
    return ValueHistoryNodeCreateSchema.parse({
      history: data.history ?? [],
      ...data
    });
  }

  export function createDefault(): ValueHistoryNode {
    return {
      history: []
    };
  }

  export function validate(data: unknown): data is ValueHistoryNode {
    return ValueHistoryNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Get the current value (most recent history entry)
   */
  export function getCurrentValue(node: ValueHistoryNode): number | undefined {
    if (node.history.length === 0) return node.bought?.value;
    return node.history[node.history.length - 1].value;
  }

  /**
   * Check if asset has been sold
   */
  export function isSold(node: ValueHistoryNode): boolean {
    return node.sold !== undefined;
  }
}
